import React, { useState, useMemo } from 'react';
import {
    Upload, Button, Steps, Table, Tag, Alert, Progress, Divider,
    Row, Col, Result, Space, Typography, Select, Tooltip,
} from 'antd';
import {
    InboxOutlined, CheckCircleOutlined, WarningOutlined,
    FileExcelOutlined, CloudUploadOutlined, BarChartOutlined,
    DownloadOutlined, ReloadOutlined,
    LinkOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { XLSX } from '../../utils/xlsxShim';
import { exporterRapportPaiements, categoriserErreur, RAISONS, ACTIONS, TAG_COLORS, couleurAction } from '../../utils/rapportUtils';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';
import PageHeader from '../../components/PageHeader';

const { Dragger } = Upload;
const { Title, Text } = Typography;

const ANNEES = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);
const fMt = (v) => Number(v ?? 0).toLocaleString(localeIntl(), { maximumFractionDigits: 0 });
// Avertissements de lecture : produits au moment du dépôt du fichier, dans la langue de l'interface.
const avert = (cle, options) => i18n.t(`reprise:avertissements.${cle}`, options);

// ── Helpers ────────────────────────────────────────────────────────
const clean = (v) => v != null ? String(v).trim().replace(/\u00a0/g, '') : null;

const dateFromRaw = (raw) => {
    if (!raw) return null;
    if (raw instanceof Date) return raw.toISOString().split('T')[0];
    if (typeof raw === 'number') {
        // Numéro de série Excel (ex: 45672 = 19 jan 2025)
        const d = XLSX.SSF.parse_date_code(raw);
        if (d && d.y) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
    }
    if (typeof raw === 'string') return raw.split('T')[0];
    return null;
};

const toNumber = (v) => {
    if (v == null) return 0;
    if (v instanceof Date) return NaN;          // une date dans une colonne montant → anomalie
    if (typeof v === 'number') return v;
    const s = String(v).replace(/[^0-9.,-]/g, '').replace(/\s/g, '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isNaN(n) ? 0 : n;
};

// ── Parser Excel - Universel (basé sur les en-têtes) ──────────────
//
// Fonctionne avec tous les formats de fichiers paiements :
//   • 2023+ (sans col "annee", sans col "BANQUE")
//   • 2022  (avec col "annee" en D et col "BANQUE" après MODE PAIEMENT)
//   • Tout futur format avec colonnes ajoutées / réordonnées
//
// La détection se fait colonne par colonne sur la ligne 1 (en-têtes)
// via normalisation + correspondance par synonymes.

// Synonymes acceptés par champ (insensibles à la casse, espaces ignorés)
const SYNONYMES_PAY = {
    sinManuel: ['N°BNCBTG', 'NBNCBTG'],
    sinHomol: ['N°BNCBHOMOLOGUE', 'NBNCBHOMOLOGUE', 'N°HOMOLOGUE', 'NHOMOLOGUE'],
    payeur: ['PAYEUR'],
    assure: ['ASSURES', 'ASSURE'],
    datePay: ['DATEPAIEMENT'],
    dateSin: ['DATESINISTRE'],
    dateEnc: ['DATEENCAISSEMENT'],
    chequeEnc: ['N°CHEQUE', 'NCHEQUE', 'VIREMENT'],   // partiel - affinement par position
    montantEnc: ['MONTANTTOTALENCAISSE', 'MONTANTENCAISSE', 'TOTALENCAISSE'],
    beneficiaire: ['BENEFICIAIRE'],
    chequePay: ['VIREMENTPAIEMENT', 'CHEQUEPAIEMENT', 'N°CHEQUEVIREMENT'],
    modePay: ['MODEDEPAIEMENT', 'MODEPAIEMENT'],
    principal: ['PRINCIPALPAYE', 'PRINCIPAL'],
    fg: ['FGPAYE', 'FG'],
    total: ['TOTALPAYE', 'TOTALPAYÉ', 'TOTAL'],
    pg: ['PRODUITSDE', 'PRODUITGESTION', 'PGTHEORIQUE'],
};

function normaliserHeader(v) {
    return String(v || '')
        .toUpperCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // retirer accents
        .replace(/[^A-Z0-9]/g, '');                          // garder lettres/chiffres
}

function construireIndexPay(rows) {
    const headers = (rows[0] || []).map(normaliserHeader);
    const idx = {};

    // Passe 1 : correspondance stricte par synonymes
    headers.forEach((h, i) => {
        for (const [champ, syns] of Object.entries(SYNONYMES_PAY)) {
            if (idx[champ] !== undefined) continue; // déjà trouvé
            if (syns.some(s => h.includes(s) || s.includes(h.slice(0, 8)))) {
                idx[champ] = i;
            }
        }
    });

    // Passe 2 : résoudre les ambiguïtés chequeEnc vs chequePay
    // chequeEnc = la première occurrence de "N°CHEQUE" / "VIREMENT"
    // chequePay = la deuxième occurrence (ou celle contenant "PAIEMENT")
    const chequeCols = [];
    headers.forEach((h, i) => {
        if (h.includes('NCHEQUE') || h.includes('VIREMENT')) chequeCols.push({ i, h });
    });
    if (chequeCols.length >= 2) {
        // Celle avec PAIEMENT → chequePay ; l'autre → chequeEnc
        const payIdx = chequeCols.findIndex(c => c.h.includes('PAIEMENT'));
        const encIdx = chequeCols.findIndex(c => !c.h.includes('PAIEMENT'));
        if (payIdx >= 0) idx.chequePay = chequeCols[payIdx].i;
        if (encIdx >= 0) idx.chequeEnc = chequeCols[encIdx].i;
    } else if (chequeCols.length === 1) {
        // Un seul N°CHEQUE → c'est le paiement
        idx.chequePay = chequeCols[0].i;
    }

    // Fallbacks sur colonnes fixes (B=1 et C=2 sont stables dans tous les formats)
    if (idx.datePay === undefined) idx.datePay = 1;
    if (idx.dateSin === undefined) idx.dateSin = 2;
    if (idx.sinManuel === undefined) idx.sinManuel = 3;

    return idx;
}

function parsePaiements(ws, anneeRef) {
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, cellDates: true });
    const data = [];

    // Construire la carte des indices depuis les en-têtes
    const I = construireIndexPay(rows);

    // Debug minimal en console (utile en cas de nouveau format)
    console.log('[ReprisePaiements] Indices détectés :', I);

    for (let i = 1; i < rows.length; i++) {
        const r = rows[i];
        if (!r) continue;

        // Ligne TOTAL (pied de tableau) → ignorée
        const cellulesCle = [r[0], r[I.sinManuel], r[I.beneficiaire], r[I.payeur]]
            .map(x => String(x ?? '').toUpperCase().trim());
        if (cellulesCle.some(c => c === 'TOTAL' || c.startsWith('TOTAL '))) continue;

        // Ligne vide
        if (!r[I.sinManuel] && (I.chequePay === undefined || !r[I.chequePay])) continue;

        const numeroChequeEmis = I.chequePay !== undefined ? clean(r[I.chequePay]) : null;
        if (!numeroChequeEmis) continue;

        const warnings = [];
        // Montants : NaN = cellule non numérique (ex. date dans une colonne
        // montant) → on signale et on neutralise à 0 (jamais de nombre géant).
        const lireMontant = (col, label) => {
            const n = toNumber(I[col] !== undefined ? r[I[col]] : 0);
            if (Number.isNaN(n)) { warnings.push(avert('nonNumerique', { libelle: label })); return 0; }
            return n;
        };
        const principalPaye = lireMontant('principal', avert('champs.principalPaye'));
        const fgPaye = lireMontant('fg', avert('champs.fgPaye'));
        const totalPaye = lireMontant('total', avert('champs.totalPaye'));
        const montant = totalPaye > 0 ? totalPaye : (principalPaye + fgPaye);

        if (!r[I.sinManuel]) warnings.push(avert('numeroSinistreManquant'));
        if (I.beneficiaire !== undefined && !r[I.beneficiaire]) warnings.push(avert('beneficiaireManquant'));
        if (!r[I.datePay]) warnings.push(avert('datePaiementManquante'));
        if (montant <= 0) warnings.push(avert('montantNul'));

        const num = clean(r[I.sinManuel]) || '';
        const lieuSurvenance = /^\d{4}\//.test(num) ? 'T'
            : /^[A-Za-z]+\//.test(num) ? 'E'
                : null;

        data.push({
            _ligne: i + 1,
            _warnings: warnings,
            _hasWarning: warnings.length > 0,
            _montant: montant,
            // Champs DTO
            datePaiement: dateFromRaw(r[I.datePay]),
            dateSinistre: dateFromRaw(r[I.dateSin]),
            numeroSinistreManuel: num,
            numeroSinistreHomologue: I.sinHomol !== undefined ? clean(r[I.sinHomol]) : null,
            organismePayeurCode: I.payeur !== undefined ? clean(r[I.payeur]) : null,
            assureNomComplet: I.assure !== undefined ? clean(r[I.assure]) : null,
            dateEncaissement: I.dateEnc !== undefined ? dateFromRaw(r[I.dateEnc]) : null,
            numeroChequeEncaissement: I.chequeEnc !== undefined ? clean(r[I.chequeEnc]) : null,
            montantEncaisse: I.montantEnc !== undefined ? toNumber(r[I.montantEnc]) : 0,
            beneficiaire: I.beneficiaire !== undefined ? clean(r[I.beneficiaire]) : null,
            modePaiement: I.modePay !== undefined
                ? (clean(r[I.modePay])?.toUpperCase() || 'CHEQUE')
                : 'CHEQUE',
            numeroChequeEmis,
            principalPaye,
            fgPaye,
            lieuSurvenance,
        });
    }
    return data;
}

// ── Colonnes ───────────────────────────────────────────────────────
const COLS = (t) => [
    {
        title: '', key: 's', width: 34, fixed: 'left',
        render: (_, r) => r._hasWarning
            ? <Tooltip title={r._warnings.join(' | ')}><WarningOutlined style={{ color: '#f97316' }} /></Tooltip>
            : <CheckCircleOutlined style={{ color: '#1a6b3c' }} />,
    },
    {
        title: t('commun.colonnes.numeroSinistre'), dataIndex: 'numeroSinistreManuel', key: 'sin', width: 150, fixed: 'left',
        render: v => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c', fontSize: 11 }}>{v}</span>
    },
    {
        title: t('paiements.colonnes.datePaiement'), dataIndex: 'datePaiement', key: 'dp', width: 110,
        render: v => v ? new Date(v).toLocaleDateString(localeIntl()) : <Tag color="red">{t('commun.manquant')}</Tag>
    },
    {
        title: t('paiements.colonnes.payeurEnc'), dataIndex: 'organismePayeurCode', key: 'pay', width: 110,
        render: v => <Tag color="geekblue" style={{ borderRadius: 20, fontSize: 11 }}>{v}</Tag>
    },
    {
        title: t('paiements.colonnes.chequeEnc'), dataIndex: 'numeroChequeEncaissement', key: 'che', width: 130,
        render: v => (
            <span style={{ fontFamily: 'var(--font-main)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                <LinkOutlined style={{ color: '#6b7280', fontSize: 10 }} />{v}
            </span>
        ),
    },
    { title: t('paiements.colonnes.beneficiaire'), dataIndex: 'beneficiaire', key: 'ben', ellipsis: true, width: 180 },
    {
        title: t('commun.colonnes.mode'), dataIndex: 'modePaiement', key: 'mode', width: 90,
        render: v => <Tag color={v === 'VIREMENT' ? 'purple' : 'blue'} style={{ borderRadius: 20, fontSize: 11 }}>{v}</Tag>
    },
    {
        title: t('paiements.colonnes.chequePaiement'), dataIndex: 'numeroChequeEmis', key: 'chp', width: 160,
        render: v => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600, fontSize: 11 }}>{v}</span>
    },
    {
        title: t('paiements.colonnes.totalPaye'), dataIndex: '_montant', key: 'mt', width: 150, align: 'right',
        render: v => <span style={{ fontWeight: 700, color: '#166534' }}>{fMt(v)}</span>
    },
    {
        title: t('commun.colonnes.principal'), dataIndex: 'principalPaye', key: 'prin', width: 130, align: 'right',
        render: v => fMt(v)
    },
    {
        title: 'FG', dataIndex: 'fgPaye', key: 'fg', width: 80, align: 'right',
        render: v => fMt(v)
    },
];

// exporterRapport → voir rapportUtils.js

// ════════════════════════════════════════════════════════════════════
//  COMPOSANT
// ════════════════════════════════════════════════════════════════════
export default function ReprisePaiements() {
    const { t } = useTranslation('reprise');
    const [step, setStep] = useState(0);
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [fileName, setFileName] = useState('');
    const [data, setData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [rapport, setRapport] = useState(null);

    // ── Stats ──────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const warnings = data.filter(r => r._hasWarning).length;
        const totalMt = data.reduce((s, r) => s + r._montant, 0);
        const byPayeur = {};
        data.forEach(r => {
            const p = r.organismePayeurCode || 'N/A';
            byPayeur[p] = (byPayeur[p] || 0) + 1;
        });
        return { total: data.length, warnings, valid: data.length - warnings, totalMt, byPayeur };
    }, [data]);

    // ── Upload ─────────────────────────────────────────────────────
    const onDrop = (file) => {
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const wb = await XLSX.read(e.target.result, { type: 'array', cellDates: true });
                const sheetName =
                    wb.SheetNames.find(n => n.trim().toUpperCase().includes('PAIEMENT') && n.includes(String(annee)))
                    || wb.SheetNames.find(n => n.trim().toUpperCase().includes('PAIMENT') && n.includes(String(annee)))
                    || wb.SheetNames.find(n => n.trim().toUpperCase().startsWith('PAIEMENT'))
                    || wb.SheetNames.find(n => n.trim().toUpperCase().startsWith('PAIMENT'));
                if (!sheetName) {
                    toast.error(t('paiements.feuilleIntrouvable', { annee, feuilles: wb.SheetNames.join(', ') }));
                    return;
                }
                const parsed = parsePaiements(wb.Sheets[sheetName], annee);
                setData(parsed);
                setStep(1);
                toast.success(t('paiements.feuilleChargee', { feuille: sheetName, nombre: parsed.length }));
            } catch (err) {
                toast.error(t('commun.erreurLectureExcel', { message: err.message }));
            }
        };
        reader.readAsArrayBuffer(file);
        return false;
    };

    // ── Import ─────────────────────────────────────────────────────
    const lancerImport = async () => {
        setImporting(true);
        setProgress(0);
        const BATCH = 50;
        let importes = 0, doublons = 0, erreurs = 0;
        const details = [];
        const lignesOk = [];

        for (let i = 0; i < data.length; i += BATCH) {
            const batch = data.slice(i, i + BATCH);
            try {
                const res = await axiosInstance.post('/v1/reprise/paiements', {
                    paiements: batch.map(r => ({
                        datePaiement: r.datePaiement,
                        dateSinistre: r.dateSinistre,
                        numeroSinistreManuel: r.numeroSinistreManuel,
                        numeroSinistreHomologue: r.numeroSinistreHomologue,
                        organismePayeurCode: r.organismePayeurCode,
                        assureNomComplet: r.assureNomComplet,
                        dateEncaissement: r.dateEncaissement,
                        numeroChequeEncaissement: r.numeroChequeEncaissement,
                        montantEncaisse: r.montantEncaisse,
                        beneficiaire: r.beneficiaire,
                        modePaiement: r.modePaiement,
                        numeroChequeEmis: r.numeroChequeEmis,
                        principalPaye: r.principalPaye,
                        fgPaye: r.fgPaye,
                        lieuSurvenance: r.lieuSurvenance,
                    })),
                });
                const d = res.data?.data;
                importes += d?.importes || 0;
                doublons += d?.doublons || 0;
                erreurs += d?.erreurs || 0;
                if (d?.details) {
                    // Retrouver la VRAIE ligne source : un même chèque peut régler
                    // plusieurs sinistres → chercher d'abord par le n° de sinistre
                    // porté par le message (créations auto, n° corrigés), sinon
                    // se rabattre sur le n° de chèque.
                    const cleNum = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                    d.details.forEach(det => {
                        const m = /créé\s*:\s*(.+)$/.exec(det.message || '')
                            || /sinistre\s+(\S+)\s+absent/.exec(det.message || '')
                            || /«\s*([^»]+?)\s*»/.exec(det.message || '');
                        let src = m ? batch.find(r => cleNum(r.numeroSinistreManuel) === cleNum(m[1])) : null;
                        if (!src) src = batch.find(r =>
                            r.numeroChequeEmis === det.numero ||
                            r.numeroSinistreManuel === det.numero);
                        details.push({
                            ...det,
                            ligne: src?._ligne,
                            beneficiaire: src?.beneficiaire,
                            datePaiement: src?.datePaiement,
                            warnings: src?._warnings || [],
                        });
                    });
                }
                if (d?.importes > 0) {
                    batch.forEach(r => {
                        const rej = d?.details?.find(dt =>
                            dt.numero === r.numeroChequeEmis || dt.numero === r.numeroSinistreManuel);
                        if (!rej || ['SINISTRE_CREE', 'VICTIME_CREEE'].includes(rej.type)) {
                            lignesOk.push(r);
                        }
                    });
                }
            } catch (err) {
                erreurs += batch.length;
                details.push({ type: 'ERROR', numero: `batch ${i}`, message: err?.response?.data?.message || err.message });
            }
            setProgress(Math.min(100, Math.round(((i + BATCH) / data.length) * 100)));
        }

        // Paiements sans encaissement lié détectés dans lignesOk
        const sansEncAuto = lignesOk.filter(r => !r.numeroChequeEncaissement).map(r => ({
            type: 'SANS_ENCAISSEMENT',
            numero: r.numeroChequeEmis || r.numeroSinistreManuel,
            ligne: r._ligne,
            beneficiaire: r.beneficiaire,
            datePaiement: r.datePaiement,
            message: t('paiements.chequeEncaissementAbsent'),
        }));
        setRapport({
            totalTraite: data.length, importes, doublons, erreurs,
            details: [...details, ...sansEncAuto],
            lignesOk, annee,
            date: new Date().toLocaleString(localeIntl())
        });
        setImporting(false);
        setStep(3);
    };

    const reset = () => { setStep(0); setData([]); setRapport(null); setFileName(''); };

    // ── RENDU ──────────────────────────────────────────────────────
    return (
        <Layout title={t('paiements.titreOnglet')}>
            <div style={{ maxWidth: 1400 }}>

                {/* Assistant d'import : pas de `compte`, l'ecran demarre vide et un
                    « 0 paiement » se lirait comme une liste vide. */}
                <PageHeader
                    titre={t('paiements.titre')}
                    icone={<CloudUploadOutlined />}
                    actions={(
                        <Select value={annee} onChange={(v) => { setAnnee(v); reset(); }}
                            style={{ width: 100, borderRadius: 8 }}
                            options={ANNEES.map(a => ({ value: a, label: String(a) }))} />
                    )}
                />

                {/* Étapes */}
                <div className="cb-card" style={{ marginBottom: 24 }}>
                    <Steps current={step} size="small" items={[
                        { title: t('commun.etapes.fichierExcel'), icon: <FileExcelOutlined /> },
                        { title: t('commun.etapes.previsualisation') },
                        { title: t('commun.etapes.confirmation') },
                        { title: t('commun.etapes.rapport'), icon: <BarChartOutlined /> },
                    ]} />
                </div>

                {/* ── Étape 0 ── */}
                {step === 0 && (
                    <div className="cb-card" style={{ textAlign: 'center', padding: 40 }}>
                        <Title level={4} style={{ color: '#1a6b3c', marginBottom: 8 }}>
                            {t('paiements.charger')}
                        </Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                            <Trans t={t} i18nKey="paiements.consigneFichier" values={{ annee }} components={{ strong: <strong /> }} />
                        </Text>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 24, fontSize: 12, color: '#f97316' }}>
                            <WarningOutlined style={{ color: '#d97706' }} /> {t('paiements.encaissementsAvant')}
                        </Text>
                        <Dragger accept=".xlsx,.xls" beforeUpload={onDrop} showUploadList={false}
                            style={{ maxWidth: 600, margin: '0 auto', borderColor: '#1a6b3c', borderRadius: 12 }}>
                            <p><InboxOutlined style={{ fontSize: 48, color: '#1a6b3c' }} /></p>
                            <p style={{ fontSize: 15, fontWeight: 600 }}>{t('commun.glisserDeposer')}</p>
                            <p style={{ color: '#6b7280', fontSize: 13 }}>{t('commun.ouCliquer')}</p>
                        </Dragger>
                    </div>
                )}

                {/* ── Étape 1 : Preview ── */}
                {step === 1 && (
                    <>
                        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                            {[
                                { label: t('paiements.totalPaiements'), value: stats.total, bg: '#e8f5ed', color: '#1a6b3c' },
                                { label: <><CheckCircleOutlined style={{ color: '#16a34a' }} /> {t('commun.valides')}</>, value: stats.valid, bg: '#dcfce7', color: '#166534' },
                                { label: <><WarningOutlined style={{ color: '#d97706' }} /> {t('commun.alertes')}</>, value: stats.warnings, bg: '#fef3c7', color: '#92400e' },
                                { label: t('commun.montantTotalAnnee', { annee }), value: fMt(stats.totalMt), bg: '#dbeafe', color: '#1e40af' },
                            ].map((s, i) => (
                                <Col xs={12} md={6} key={i}>
                                    <div style={{ background: s.bg, borderRadius: 12, padding: '14px 18px' }}>
                                        <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                                        <div style={{ fontSize: 12 }}>{s.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {/* Répartition payeurs */}
                        <div className="cb-card" style={{ marginBottom: 16, padding: '14px 20px' }}>
                            <div style={{ fontWeight: 700, color: '#1a6b3c', marginBottom: 10, fontSize: 13 }}>
                                {t('paiements.repartitionPayeur')}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {Object.entries(stats.byPayeur).sort((a, b) => b[1] - a[1]).map(([p, n]) => (
                                    <Tag key={p} color="geekblue" style={{ borderRadius: 20, fontSize: 12, padding: '3px 10px' }}>
                                        {p} : <strong>{n}</strong>
                                    </Tag>
                                ))}
                            </div>
                        </div>

                        <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
                            message={t('paiements.liaisonTitre')}
                            description={
                                <span>
                                    <Trans t={t} i18nKey="paiements.liaisonDescription" components={{ strong: <strong /> }} />
                                </span>
                            } />

                        <div className="cb-card" style={{ padding: 0 }}>
                            <div style={{ padding: '14px 20px 8px', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 13 }}>
                                    {t('paiements.titreTableau', { annee, nombre: data.length, fichier: fileName })}
                                </span>
                            </div>
                            <Table className="cb-table" dataSource={data.map((r, i) => ({ ...r, key: i }))}
                                columns={COLS(t)} size="small" scroll={{ x: 1400, y: 420 }}
                                pagination={{ pageSize: 50, showTotal: (n) => t('paiements.nbPaiements', { nombre: n }) }}
                                rowClassName={r => r._hasWarning ? 'ant-table-row-warning' : ''} />
                        </div>

                        <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
                            <Button onClick={reset} style={{ borderRadius: 8 }}>{t('commun.recharger')}</Button>
                            <Button type="primary" onClick={() => setStep(2)}
                                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8, height: 40, paddingInline: 24 }}>
                                {t('commun.passerConfirmation')}
                            </Button>
                        </div>
                    </>
                )}

                {/* ── Étape 2 : Confirmation ── */}
                {step === 2 && (
                    <div className="cb-card" style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center', padding: 40 }}>
                        <Title level={3} style={{ color: '#1a6b3c', marginBottom: 8 }}><CheckCircleOutlined style={{ color: '#16a34a' }} /> {t('commun.confirmerImport')}</Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 28 }}>
                            <Trans t={t} i18nKey="paiements.consigneConfirmation" values={{ nombre: stats.total }}
                                components={{ strong: <strong />, br: <br /> }} />
                        </Text>

                        <Row gutter={[16, 16]} style={{ marginBottom: 28, textAlign: 'left' }}>
                            {[
                                { label: t('paiements.totalPaiements'), value: stats.total, bg: '#e8f5ed', color: '#1a6b3c' },
                                { label: t('commun.montantTotalFcfa'), value: fMt(stats.totalMt), bg: '#dbeafe', color: '#1e40af' },
                                { label: t('commun.valides'), value: stats.valid, bg: '#dcfce7', color: '#166534' },
                                { label: t('commun.alertes'), value: stats.warnings, bg: '#fef3c7', color: '#92400e' },
                            ].map((s, i) => (
                                <Col span={12} key={i}>
                                    <div style={{ background: s.bg, borderRadius: 10, padding: '12px 16px' }}>
                                        <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                                        <div style={{ fontSize: 12 }}>{s.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {importing && (
                            <div style={{ marginBottom: 24 }}>
                                <Progress percent={progress} status="active" strokeColor="#1a6b3c" />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {t('commun.importEnCours', { fait: Math.round(progress * stats.total / 100), total: stats.total })}
                                </Text>
                            </div>
                        )}
                        {!importing && (
                            <Space>
                                <Button onClick={() => setStep(1)} style={{ borderRadius: 8 }}>{t('commun.retour')}</Button>
                                <Button type="primary" size="large" icon={<CloudUploadOutlined />}
                                    onClick={lancerImport}
                                    style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8, paddingInline: 28 }}>
                                    {t('paiements.lancerImport', { nombre: stats.total })}
                                </Button>
                            </Space>
                        )}
                    </div>
                )}

                {/* ── Étape 3 : Rapport ── */}
                {step === 3 && rapport && (
                    <>
                        <Result
                            icon={rapport.erreurs === 0
                                ? <CheckCircleOutlined style={{ color: '#1a6b3c' }} />
                                : <WarningOutlined style={{ color: '#f97316' }} />}
                            status={rapport.erreurs === 0 ? 'success' : 'warning'}
                            title={t('paiements.importTermine', { nombre: rapport.importes })}
                            subTitle={t('paiements.sousTitreRapport', { annee, date: rapport.date })}
                        />

                        <div className="cb-card" style={{ maxWidth: 700, margin: '0 auto 20px' }}>
                            <Title level={4} style={{ color: '#1a6b3c', marginBottom: 20 }}>
                                {t('paiements.titreRapport', { annee })}
                            </Title>
                            <Row gutter={[16, 16]}>
                                {[
                                    { label: t('commun.totalTraite'), value: rapport.totalTraite, bg: '#f4f6f5', color: '#374151' },
                                    { label: <><CheckCircleOutlined style={{ color: '#16a34a' }} /> {t('commun.importes')}</>, value: rapport.importes, bg: '#dcfce7', color: '#166534' },
                                    { label: t('commun.doublonsIcone'), value: rapport.doublons, bg: '#fef3c7', color: '#92400e' },
                                    { label: <><CloseCircleOutlined style={{ color: '#dc2626' }} /> {t('commun.erreurs')}</>, value: rapport.erreurs, bg: '#fee2e2', color: '#991b1b' },
                                    { label: <><WarningOutlined style={{ color: '#d97706' }} /> {t('paiements.sansEncaissement')}</>, value: rapport.details?.filter(d => d.type === 'SANS_ENCAISSEMENT').length || 0, bg: '#f3e8ff', color: '#6b21a8' },
                                ].map((s, i) => (
                                    <Col span={6} key={i}>
                                        <div style={{
                                            background: s.bg, borderRadius: 10, padding: '14px 16px',
                                            borderLeft: `4px solid ${s.color}`
                                        }}>
                                            <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                                            <div style={{ fontSize: 12 }}>{s.label}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            {/* Sans encaissement */}
                            {rapport.details?.filter(d => d.type === 'SANS_ENCAISSEMENT').length > 0 && (
                                <Alert type="warning" showIcon style={{ marginTop: 12, borderRadius: 8 }}
                                    message={t('paiements.sansEncaissementLie', { count: rapport.details.filter(d => d.type === 'SANS_ENCAISSEMENT').length })}
                                    description={t('paiements.sansEncaissementDescription')} />
                            )}

                            {/* Tableau anomalies */}
                            {rapport.details?.filter(d => d.type !== 'SANS_ENCAISSEMENT').length > 0 && (
                                <div style={{ marginTop: 16 }}>
                                    <Divider><Text type="secondary" style={{ fontSize: 12 }}>
                                        {t('commun.anomaliesLignes', { count: rapport.details.filter(d => d.type !== 'SANS_ENCAISSEMENT').length })}
                                    </Text></Divider>
                                    <Table size="small" scroll={{ x: 800 }}
                                        dataSource={rapport.details
                                            .filter(d => d.type !== 'SANS_ENCAISSEMENT')
                                            .slice(0, 50)
                                            .map((d, i) => ({ ...d, key: i, _cat: categoriserErreur(d) }))}
                                        columns={[
                                            {
                                                title: t('commun.colonnes.ligne'), dataIndex: 'ligne', width: 60,
                                                render: v => v ? <code>L{v}</code> : '-'
                                            },
                                            {
                                                title: t('commun.colonnes.type'), dataIndex: 'type', width: 120,
                                                render: v => <Tag color={TAG_COLORS[v] || 'default'} style={{ fontSize: 10 }}>{v}</Tag>
                                            },
                                            {
                                                title: t('commun.colonnes.numeroReference'), dataIndex: 'numero', width: 170,
                                                render: v => <code style={{ fontSize: 11 }}>{v || '-'}</code>
                                            },
                                            {
                                                title: t('commun.colonnes.raison'), key: 'raison', ellipsis: true,
                                                render: (_, r) => RAISONS[r._cat] || r.message
                                            },
                                            {
                                                title: t('commun.colonnes.actionCorrective'), key: 'action', width: 240, ellipsis: true,
                                                render: (_, r) => <span style={{ fontSize: 11, color: couleurAction(r._cat) }}>
                                                    {ACTIONS[r._cat] || t('commun.interventionManuelle')}
                                                </span>
                                            },
                                        ]}
                                        pagination={{ pageSize: 10, showTotal: (n) => t('commun.nbAnomalies', { count: n }) }}
                                    />
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Button icon={<DownloadOutlined />} onClick={() => exporterRapportPaiements(rapport, annee)}
                                    style={{ borderRadius: 8 }}>
                                    {t('commun.exporterRapport')}
                                </Button>
                                <Button icon={<ReloadOutlined />} onClick={reset} style={{ borderRadius: 8 }}>
                                    {t('commun.nouvelleReprise')}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <style>{`
        .ant-table-row-warning td { background: #fff7ed !important; }
        .ant-table-row-warning:hover td { background: #fef3c7 !important; }
      `}</style>
        </Layout>
    );
}